/* eslint-disable camelcase */
import { Controller } from '@hotwired/stimulus'
import { useClickOutside } from 'stimulus-use'

import { nonProviders, getProviderIdFromName } from '../../utils/providers'
import { PROVIDERS } from '../../utils/providers-listing'
import { getAddresses, selectAddress, validatePostcode, ADDRESS_STATE, PROVIDER_STATE } from '../services/address'
import { selectProvider } from '../services/providers'
import { track, trackSegment } from '../utils/track'
import debounce from '~/utils/debounce'
import { formatPostcode, isValidPostcode } from '~/utils/postcode'
import { addressEntryForm, addressEntryBroadbandSpecificFields } from '~/utils/tracking-constants'

const USER_INPUT_DEBOUNCE = 1000
const POSTCODE_DEBOUNCE_MS = 460 // needs to be under 500ms to not trigger CLS.

const showElement = (elem) => {
  elem.classList.remove('element-hidden')
}

const hideElement = (elem) => {
  elem.classList.add('element-hidden')
}

const clearAddresses = (scope) => {
  scope.addressesTarget.setAttribute('data-addresses', '')
  scope.addressesTarget.setAttribute('data-postcode', '')
  scope.addressesTarget.querySelector('ul').innerHTML = ''
}

const postcodeOnChange = async (e, scope) => {
  // clear the address object attribute
  hideElement(scope.addressesTarget)
  hideElement(scope.addressesUnderlayTarget)

  hideElement(scope.missingPostcodeErrorTarget)
  hideElement(scope.missingProviderErrorTarget)

  hideElement(scope.noResultsErrorTarget)
  scope.hideError()

  if (scope.postcodeTarget.value.length === 0) {
    hideElement(scope.addressBoxTarget)
    hideElement(scope.clearPostcodeBoxButtonTarget)
    scope.hideError()
  } else {
    // open the box as soon as the postcode isn't empty
    scope.addressLoadingState()
    showElement(scope.clearPostcodeBoxButtonTarget)

    const debounceIsValidPostcode = () => {
      if (!isValidPostcode(scope.postcodeTarget.value)) {
        clearAddresses(scope)
        scope.errorPostcodeState(e)
      }
    }

    debounce(debounceIsValidPostcode, USER_INPUT_DEBOUNCE)()
  }

  // format the postcode:
  scope.postcodeTarget.value = formatPostcode(scope.postcodeTarget.value)

  const currentPostcode = scope.postcodeTarget.value

  if (validatePostcode(currentPostcode) === ADDRESS_STATE) {
    try {
      if (scope.addressesTarget.getAttribute('data-postcode') !== currentPostcode) {
        clearAddresses(scope)

        const addresses = await getAddresses(currentPostcode)

        // set the address object attribute
        scope.addressesTarget.setAttribute('data-addresses', JSON.stringify(addresses))
        scope.addressesTarget.setAttribute('data-postcode', currentPostcode)

        const elementTemplate = scope.hiddenListElementTarget.innerHTML

        addresses.forEach((address, index) => {
          scope.addressesTarget.querySelector('ul').innerHTML += elementTemplate
            .replace('ELEMENT_TEXT', address.summaryLine)
            .replace('ELEMENT_INDEX', index)
        })
      }

      hideElement(scope.loadingTarget)
      showElement(scope.addressesTarget)

      showElement(scope.addressesUnderlayTarget)
    } catch (error) {
      hideElement(scope.loadingTarget)
      showElement(scope.noResultsErrorTarget)
      scope.postcodeTarget.setAttribute('data-error', true)
    }
  }
}

export default class PostcodeFormVariantController extends Controller {
  static targets = [
    'clearPostcodeBoxButton',
    'postcode',
    'clearAddressButton',
    'addressInput',
    'addressBox',
    'addressSelected',
    'loading',
    'error',
    'addresses',
    'dropdown',
    'provider',
    'form',
    'noResultsError',
    'addressesUnderlay',
    'missingPostcodeError',
    'missingProviderError',
    'addressSelectedLoading',
    'providerSelectedLoading',
    'hiddenListElement'
  ]

  static values = { formId: String }

  connect() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useClickOutside(this, { element: this.dropdownTarget })

    this.debouncedChangePostcode = debounce((e) => {
      track('Broadband - Address Capture', 'Editing address')

      postcodeOnChange(e, this)
    }, POSTCODE_DEBOUNCE_MS)
  }

  get formName() {
    return this.formTarget.getAttribute('data-src') || this.formTarget.dataset.trackingId || this.formIdValue
  }

  get formId() {
    return this.formTarget.getAttribute('data-src-id') || this.formIdValue
  }

  get landingFormProps() {
    return addressEntryForm({
      formId: this.formName,
      formName: this.formName,
      formStartedId: this.formId
    })
  }

  initialize() {
    this.backToPostcodeEditMode()
    this.errorTarget.setAttribute('style', 'display:block;')
    hideElement(this.clearPostcodeBoxButtonTarget)
    this.hideError()
  }

  postcodeState() {
    this.addressInputTarget.value = ''

    this.hideError()

    hideElement(this.clearPostcodeBoxButtonTarget)
    hideElement(this.clearAddressButtonTarget)
    hideElement(this.addressInputTarget)
    hideElement(this.addressBoxTarget)

    showElement(this.postcodeTarget.parentElement)
  }

  filledInPostcodeState() {
    this.postcodeState()

    showElement(this.addressesUnderlayTarget)

    showElement(this.addressesTarget)
    hideElement(this.loadingTarget)

    showElement(this.addressBoxTarget)
    showElement(this.clearPostcodeBoxButtonTarget)
  }

  changePostcode(e) {
    this.debouncedChangePostcode(e)
  }

  clickPostcode(e) {
    this.toggleDropdown(e, false)

    if (
      this.addressesTarget.getAttribute('data-addresses') &&
      !this.addressesTarget.classList.contains('element-hidden')
    ) {
      return
    }

    postcodeOnChange(e, this)
  }

  editAddress(e) {
    track('Broadband - Address Capture', 'Editing address')

    this.toggleDropdown(e, false)
    this.filledInPostcodeState()
  }

  errorPostcodeState(e) {
    e.preventDefault()

    track('Broadband - Postcode Capture', 'Incorrect Postcode')
    trackSegment('Form Errored', {
      submit_id: this.formId,
      form_name: this.formName,
      form_started_id: this.formId,
      form_error_text: 'Please enter your postcode e.g. SE1 2LH'
    })

    showElement(this.errorTarget)
    hideElement(this.loadingTarget)

    this.postcodeTarget.setAttribute('data-error', true)
  }

  hideError() {
    hideElement(this.errorTarget)
    showElement(this.loadingTarget)

    this.postcodeTarget.removeAttribute('data-error')
  }

  addressLoadingState() {
    showElement(this.addressBoxTarget)
    showElement(this.addressBoxTarget.parentElement)
    showElement(this.loadingTarget)
  }

  clearPostcodeBox(e) {
    track('Broadband - Address Capture', 'Clear address')

    e.preventDefault()

    this.backToPostcodeEditMode()
    this.postcodeTarget.value = ''

    this.hideError()
    hideElement(this.clearPostcodeBoxButtonTarget)
  }

  backToPostcodeEditMode() {
    showElement(this.clearPostcodeBoxButtonTarget)
    hideElement(this.clearAddressButtonTarget)
    this.addressInputTarget.value = ''
    hideElement(this.addressInputTarget)
    showElement(this.postcodeTarget.parentElement)
    hideElement(this.addressBoxTarget)

    hideElement(this.addressesUnderlayTarget)
  }

  closeAddressDropdown() {
    hideElement(this.addressesUnderlayTarget)
    hideElement(this.addressesTarget)
    hideElement(this.addressBoxTarget)
  }

  scrollToTopOfInputForm() {
    this.scrollTop = document.documentElement.scrollTop
    this.topOfForm = document.getElementById('input-form').getBoundingClientRect().top + this.scrollTop

    if (this.topOfForm < this.scrollTop) {
      document.documentElement.scrollTo({ top: this.topOfForm - 16, behavior: 'smooth' })
    }
  }

  async clickAddress(e) {
    hideElement(this.missingPostcodeErrorTarget)
    this.addressesTarget.toggleAttribute('data-dropdown-loading')

    const selectedAddressIndex = e.target.parentElement.getAttribute('data-address-index')

    const addresses = JSON.parse(this.addressesTarget.getAttribute('data-addresses'))
    const selectedAddress = addresses[selectedAddressIndex]

    this.addressInputTarget.querySelector('div').innerHTML = selectedAddress.summaryLine

    const selectAddressCheck = await selectAddress(selectedAddress, this.formId)

    hideElement(this.addressesUnderlayTarget)

    if (selectAddressCheck === PROVIDER_STATE) {
      if (selectedAddress.uprn || selectedAddress.udprn) {
        track('Broadband - Address Capture', 'Address selected')
      } else {
        track('Broadband - Address Capture', 'Address not listed')
        trackSegment('Element Clicked', {
          element_name: 'Address Not Listed',
          element_text: 'Address Not Listed'
        })
      }

      this.addressesTarget.toggleAttribute('data-dropdown-loading')

      hideElement(this.postcodeTarget.parentElement)
      hideElement(this.addressBoxTarget)

      showElement(this.addressInputTarget)
      showElement(this.dropdownTarget)

      this.scrollToTopOfInputForm()
    }
  }

  toggleDropdown(_e, forceVisibility = null) {
    const elem = this.dropdownTarget.querySelector('[data-dropdown-element="menu"]')

    if (forceVisibility === true) {
      elem.setAttribute('data-dropdown-shown', true)
    } else if (forceVisibility === false) {
      elem.removeAttribute('data-dropdown-shown')
    } else {
      elem.toggleAttribute('data-dropdown-shown')
    }
  }

  keydownDropdownToggle(e) {
    if (e.code === 'Enter') {
      this.toggleDropdown(e, true)
      this.listItems = this.dropdownTarget.querySelectorAll('[role="menuitemcheckbox"]')
      this.listItems[1].focus()
    }
  }

  async setProvider(e, selectedProviderId, selectedProviderText) {
    const selectProviderResult = await selectProvider(selectedProviderId, this.formId)

    if (selectProviderResult === 'results') {
      track('Broadband - Provider Capture', selectedProviderText)
      this.providerTarget.setAttribute('value', selectedProviderText)
      this.providerTarget.value = selectedProviderText

      this.providerTarget.querySelector('select option').value = selectedProviderText
      this.providerTarget.querySelector('select option').innerHTML = selectedProviderText

      this.toggleDropdown(e)
      hideElement(this.missingPostcodeErrorTarget)
      hideElement(this.missingProviderErrorTarget)
      hideElement(this.providerSelectedLoadingTarget)
      this.scrollToTopOfInputForm()
    }
  }

  dropdownSelect(e) {
    if (e.target.getAttribute('role') !== 'menuitemcheckbox') return

    showElement(this.providerSelectedLoadingTarget)

    const selectedProviderId = e.target.parentElement.parentElement.getAttribute('data-value')
    const selectedProviderText = e.target.parentElement.parentElement.textContent

    this.setProvider(e, selectedProviderId, selectedProviderText)
  }

  keydownDropdownSelect(e) {
    e.stopPropagation()

    if (e.code === 'Escape') {
      this.toggleDropdown(false)
    }

    if (e.code === 'Enter') {
      showElement(this.providerSelectedLoadingTarget)

      const selectedProviderId = e.target.parentElement.getAttribute('data-value')
      const selectedProviderText = e.target.parentElement.textContent

      this.setProvider(e, selectedProviderId, selectedProviderText)

      this.providerTarget.focus()
    }
  }

  isFormValid() {
    return (
      validatePostcode(this.postcodeTarget.value) === ADDRESS_STATE &&
      this.addressSelectedTarget.textContent &&
      this.providerTarget.value
    )
  }

  hasAddressSelected() {
    return !!this.addressSelectedTarget.textContent
  }

  submit(e) {
    e.preventDefault()

    if (validatePostcode(this.postcodeTarget.value) !== ADDRESS_STATE) {
      showElement(this.missingPostcodeErrorTarget)
      return this.errorPostcodeState(e)
    }

    if (this.isFormValid()) {
      track('Broadband - Availability Checker', 'Submit')

      const formStepField = [
        {
          id: 'address-selected',
          name: 'address-selected',
          label: '',
          value: this.addressSelectedTarget.textContent,
          type: 'text'
        },
        {
          id: 'current-provider',
          name: 'current-provider',
          label: '',
          value: this.providerTarget.value,
          type: 'text'
        }
      ]

      const broadbandSpecificFields = addressEntryBroadbandSpecificFields({
        address: {
          postcode: formatPostcode(this.postcodeTarget.value),
          summaryLine: this.addressSelectedTarget.textContent
        },
        providerId: getProviderIdFromName([...PROVIDERS, ...nonProviders], this.providerTarget.value),
        providerName: this.providerTarget.value
      })

      trackSegment('Form Submitted', {
        ...this.landingFormProps,
        form_step_field: formStepField
      })

      trackSegment('Broadband Form Submitted', {
        ...this.landingFormProps,
        form_step_field: formStepField,
        ...broadbandSpecificFields
      })

      // If the user has chosen one of the pills above the table, then apply the selected filter to the results page
      if (this.formName === 'featureFilterCheapestModal') {
        this.formTarget.setAttribute('action', this.formTarget.getAttribute('action') + '?monthlyCost=0-25')
      } else if (this.formName === 'featureFilterFastestModal') {
        this.formTarget.setAttribute('action', this.formTarget.getAttribute('action') + '?order=SPEED_DESC')
      }

      if (this.postcodeTarget.value.toUpperCase().startsWith('HU')) {
        // BB Connect are redirecting all Hull postcodes to the hull landing page
        window.location = '/broadband/hull/'
      } else {
        window.location = this.formTarget.getAttribute('action')
      }
    } else {
      console.log('Form not valid')
      if (!this.hasAddressSelected()) showElement(this.missingPostcodeErrorTarget)
      else showElement(this.missingProviderErrorTarget)
    }
  }
}
